import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ForkKnife, ShieldCheck, WarningCircle, CheckCircle, Spinner, Package, ShoppingBag,
} from '@phosphor-icons/react';
import logisticsApi from '../../lib/logisticsApi';
import SimCard from './SimCard';

const fmt = (minor) =>
  ((minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Line = ({ label, value, strong, accent }) => (
  <div className="flex items-baseline justify-between py-1.5">
    <span className="text-sm" style={{ color: strong ? 'var(--ls-secondary)' : 'var(--ls-muted)', fontWeight: strong ? 600 : 400 }}>
      {label}
    </span>
    <span className="ls-mono" style={{ fontSize: strong ? 16 : 13, fontWeight: strong ? 700 : 500, color: accent ? 'var(--ls-primary)' : 'var(--ls-secondary)' }}>
      Ÿ{fmt(value)}
    </span>
  </div>
);

const DELIVERY_TYPES = [
  { key: 'dine_in', label: 'Dine In', Icon: ForkKnife },
  { key: 'takeaway', label: 'Takeaway', Icon: ShoppingBag },
  { key: 'delivery', label: 'Delivery', Icon: Package },
];

const FoodOrderDrawer = ({
  open, onClose, exerciseId, venue, items = [], wallets = [],
  currencySymbol = 'Ÿ', onOrdered,
}) => {
  const [deliveryType, setDeliveryType] = useState('dine_in');
  const [walletId, setWalletId] = useState('');
  const [card, setCard] = useState(null);
  const [cvv, setCvv] = useState('');
  const [notes, setNotes] = useState('');

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(null);

  const wallet = useMemo(
    () => wallets.find((w) => w.wallet_id === walletId) || null,
    [wallets, walletId],
  );

  useEffect(() => {
    if (walletId || wallets.length === 0) return;
    const best = [...wallets].sort((a, b) => (b.available || 0) - (a.available || 0))[0];
    setWalletId(best.wallet_id);
  }, [wallets, walletId]);

  useEffect(() => {
    if (!open) return;
    setCvv('');
    setSubmitError(null);
    setSuccess(null);
    setNotes('');
  }, [open, venue?.venue_id]);

  useEffect(() => {
    if (!walletId) { setCard(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const c = await logisticsApi.walletSimCard(walletId);
        if (!cancelled) setCard(c);
      } catch { if (!cancelled) setCard(null); }
    })();
    return () => { cancelled = true; };
  }, [walletId]);

  const fetchQuote = useCallback(async () => {
    if (!open || !exerciseId || !venue?.venue_id || items.length === 0) return;
    setQuoting(true);
    setQuoteError(null);
    try {
      const q = await logisticsApi.quoteFood(exerciseId, {
        venue_id: venue.venue_id,
        items: items.map((i) => ({ item_id: i.item_id, quantity: i.quantity })),
        delivery_type: deliveryType,
      });
      setQuote(q);
    } catch (e) {
      setQuote(null);
      setQuoteError(e.message || 'Could not price this order');
    } finally {
      setQuoting(false);
    }
  }, [open, exerciseId, venue?.venue_id, items, deliveryType]);

  useEffect(() => {
    const t = setTimeout(fetchQuote, 250);
    return () => clearTimeout(t);
  }, [fetchQuote]);

  const total = quote?.total_minor || 0;
  const balanceBefore = wallet?.available || 0;
  const balanceAfter = balanceBefore - total;
  const canAfford = balanceAfter >= 0;
  const cvvReady = /^\d{3}$/.test(cvv);
  const ready = !!quote && !quoting && !!walletId && canAfford && cvvReady && !submitting;

  const authorise = async () => {
    if (!ready) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const idem = `ford:${exerciseId}:${venue.venue_id}:${walletId}:${Date.now()}`;
      const held = await logisticsApi.createFoodOrder(exerciseId, {
        venue_id: venue.venue_id,
        items: items.map((i) => ({ item_id: i.item_id, quantity: i.quantity })),
        delivery_type: deliveryType,
        wallet_id: walletId,
        cvv,
        idempotency_key: idem,
        notes: notes.trim() || null,
      });
      const confirmed = await logisticsApi.confirmFoodOrder(held.order.order_id);
      setSuccess(confirmed.order);
      setCvv('');
      onOrdered?.(confirmed.order, confirmed.wallet);
    } catch (e) {
      setSubmitError(e.message || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          />
          <motion.aside
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[460px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
            data-testid="ls-food-order-drawer"
            role="dialog" aria-label="Food order checkout"
          >
            <div className="flex items-center justify-between px-5 h-14 shrink-0" style={{ borderBottom: '1px solid var(--ls-border)' }}>
              <div className="flex items-center gap-2">
                <ForkKnife size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">Food Order</span>
              </div>
              <button onClick={onClose} className="ls-btn-ghost !p-2" aria-label="Close">
                <X size={14} weight="bold" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5">
              {success ? (
                <div className="text-center py-10" data-testid="ls-food-order-success">
                  <CheckCircle size={56} weight="duotone" style={{ color: 'var(--ls-success)' }} />
                  <h3 className="ls-heading text-xl font-bold mt-4">Order confirmed</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--ls-muted)' }}>
                    {success.venue_snapshot?.name}
                  </p>
                  <p className="ls-mono text-3xl font-bold mt-4" style={{ color: 'var(--ls-primary)' }}>
                    {currencySymbol}{fmt(success.total_minor)}
                  </p>
                  <p className="ls-mono text-[11px] mt-3" style={{ color: 'var(--ls-muted)' }}>
                    {success.order_id}
                  </p>
                  <button className="ls-btn-primary mt-6" onClick={onClose} data-testid="ls-food-order-done">
                    Done
                  </button>
                </div>
              ) : (
                <>
                  {/* Venue header */}
                  <div className="flex gap-3 mb-5">
                    <div
                      className="w-20 h-20 shrink-0 rounded"
                      style={{
                        background: `url(${venue?.photo}) center / cover, var(--ls-elevated)`,
                        border: '1px solid var(--ls-border)',
                      }}
                    />
                    <div className="min-w-0">
                      <p className="ls-overline">{(venue?.subcategory || 'restaurant').replace(/_/g, ' ')}</p>
                      <h3 className="ls-heading text-lg font-bold leading-tight truncate">{venue?.name}</h3>
                      {venue?.address && (
                        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--ls-muted)' }}>{venue.address}</p>
                      )}
                    </div>
                  </div>

                  {/* Delivery type */}
                  <div className="mb-4">
                    <span className="ls-overline mb-2 block">Order type</span>
                    <div className="flex gap-2">
                      {DELIVERY_TYPES.map(({ key, label, Icon }) => (
                        <button
                          key={key}
                          className="flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
                          style={{
                            background: deliveryType === key ? 'var(--ls-primary)' : 'var(--ls-elevated)',
                            color: deliveryType === key ? '#fff' : 'var(--ls-muted)',
                            border: `1px solid ${deliveryType === key ? 'var(--ls-primary)' : 'var(--ls-border)'}`,
                          }}
                          onClick={() => setDeliveryType(key)}
                        >
                          <Icon size={13} weight="bold" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Order items */}
                  <div className="ls-elevated p-4 mb-4 rounded-lg" data-testid="ls-food-order-items">
                    <p className="ls-overline mb-2">Your order</p>
                    {items.map((item) => (
                      <div key={item.item_id} className="flex items-baseline justify-between py-1">
                        <span className="text-sm" style={{ color: 'var(--ls-secondary)' }}>
                          {item.quantity}× {item.name}
                        </span>
                        <span className="ls-mono text-xs" style={{ color: 'var(--ls-muted)' }}>
                          {currencySymbol}{fmt(item.price_minor * item.quantity)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Price breakdown */}
                  <div className="ls-elevated p-4 mb-4 rounded-lg" data-testid="ls-food-order-breakdown">
                    {quoting ? (
                      <div className="flex items-center gap-2 py-4 justify-center">
                        <Spinner size={14} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
                        <span className="text-sm" style={{ color: 'var(--ls-muted)' }}>Pricing…</span>
                      </div>
                    ) : quoteError ? (
                      <p className="text-sm py-2" style={{ color: 'var(--ls-danger)' }}>{quoteError}</p>
                    ) : quote ? (
                      <>
                        <Line label="Subtotal" value={quote.subtotal_minor} />
                        <Line label="Simulated tax" value={quote.tax_minor} />
                        <Line label="Service fee" value={quote.service_fee_minor} />
                        <div style={{ borderTop: '1px solid var(--ls-border)', marginTop: 6, paddingTop: 6 }}>
                          <Line label="Total" value={quote.total_minor} strong accent />
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* Notes */}
                  <label className="block mb-4">
                    <span className="ls-overline mb-1 block">Special instructions</span>
                    <textarea
                      className="ls-input w-full text-sm" rows={2}
                      placeholder="Allergies, preferences..."
                      value={notes} onChange={(e) => setNotes(e.target.value)}
                    />
                  </label>

                  {/* Wallet picker */}
                  <label className="block mb-4">
                    <span className="ls-overline mb-1 block">Pay from</span>
                    <select value={walletId} onChange={(e) => setWalletId(e.target.value)}
                      className="ls-input w-full" data-testid="ls-food-order-wallet">
                      {wallets.length === 0 && <option value="">No wallet available</option>}
                      {wallets.map((w) => (
                        <option key={w.wallet_id} value={w.wallet_id}>
                          {w.owner_type === 'team' ? 'Team' : 'Personal'} · Ÿ{fmt(w.available)} available
                          {w.frozen ? ' · FROZEN' : ''}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Card + CVV */}
                  <div className="grid grid-cols-2 gap-3 mb-4 items-start">
                    <SimCard card={card} holder={card?.holder} compact />
                    <div>
                      <span className="ls-overline mb-1 block">Security code</span>
                      <input
                        type="text" inputMode="numeric" maxLength={3}
                        value={cvv} placeholder="•••"
                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        className="ls-input w-full ls-mono text-center"
                        style={{ fontSize: 20, letterSpacing: '0.35em' }}
                        data-testid="ls-food-order-cvv" aria-label="Card security code"
                      />
                      <p className="text-[11px] mt-2 leading-snug" style={{ color: 'var(--ls-muted)' }}>
                        Read the code off your simulated card
                        {card?.cvv ? <> — <span className="ls-mono">{card.cvv}</span></> : null}.
                      </p>
                    </div>
                  </div>

                  {/* Balance ledger */}
                  {wallet && quote && (
                    <div className="ls-surface p-4 rounded-lg" style={{ borderColor: canAfford ? 'var(--ls-border)' : 'var(--ls-danger)' }}>
                      <p className="ls-overline mb-2">Balance</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[11px]" style={{ color: 'var(--ls-muted)' }}>Before</p>
                          <p className="ls-mono text-lg font-semibold">Ÿ{fmt(balanceBefore)}</p>
                        </div>
                        <span style={{ color: 'var(--ls-muted)' }}>→</span>
                        <div className="text-right">
                          <p className="text-[11px]" style={{ color: 'var(--ls-muted)' }}>After</p>
                          <p className="ls-mono text-lg font-bold"
                            style={{ color: canAfford ? 'var(--ls-primary)' : 'var(--ls-danger)' }}>
                            Ÿ{fmt(balanceAfter)}
                          </p>
                        </div>
                      </div>
                      {!canAfford && (
                        <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                          <WarningCircle size={13} weight="fill" />
                          Not enough available credit in this wallet.
                        </p>
                      )}
                    </div>
                  )}

                  {submitError && (
                    <p className="text-sm mt-4 flex items-start gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                      <WarningCircle size={15} weight="fill" className="shrink-0 mt-0.5" />
                      {submitError}
                    </p>
                  )}
                </>
              )}
            </div>

            {!success && (
              <div className="px-5 py-4 shrink-0" style={{ borderTop: '1px solid var(--ls-border)' }}>
                <button
                  className="ls-btn-primary w-full justify-center"
                  disabled={!ready} onClick={authorise}
                  data-testid="ls-food-order-authorise"
                >
                  {submitting ? (
                    <><Spinner size={15} className="animate-spin" /> Authorising…</>
                  ) : (
                    <><ShieldCheck size={15} weight="fill" /> Authorise {currencySymbol}{fmt(total)}</>
                  )}
                </button>
                <p className="text-[11px] text-center mt-2" style={{ color: 'var(--ls-muted)' }}>
                  Funds are held, then captured on confirmation.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default FoodOrderDrawer;
